// Steuerpaket S3: Bausteine der Büro-Ansicht (Objekt › Finanzen, Eigentümer › Steuer).
import { useState } from 'react';
import { ArrowCounterClockwise, CaretDown, CaretRight, Check, Prohibit, Question, Scales } from '@phosphor-icons/react';
import { Auswahl, Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, SegmentedControl, TextFeld } from '../immobilien/ui';
import { eur } from '../../lib/immobilien/format';
import { loescheKlaerung, speichereKlaerung, speichereSchluessel, type KlaerungZeile } from '../../lib/buchhaltung/api';
import {
  EINNAHME_KATEGORIEN, KATEGORIE_KURZ, KATEGORIE_TEXT, KOSTEN_KATEGORIEN, NICHT_ANLAGE_V_TEXT,
  type Kategorie, type NichtAnlageV, type ObjektErgebnis, type Ungeklaert,
} from '../../lib/buchhaltung/steuerpaket';
import type { Kostenschluessel, SchluesselArt, WegEinheit, WegVerteilung } from '../../lib/buchhaltung/weg';

const datum = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '–');
const leise = { fontSize: 12, color: 'var(--ink3)' } as const;

export function JahrWahl({ jahre, wert, onChange }: { jahre: readonly number[]; wert: number; onChange: (j: number) => void }) {
  return (
    <SegmentedControl ariaLabel="Steuerjahr" hoehe={34} wert={String(wert)} onChange={(k) => onChange(Number(k))}
      segmente={jahre.slice(0, 5).map((j) => ({ key: String(j), label: String(j) }))} />
  );
}

/** Pflichthinweis (Björn, Regel 8). */
export function KeineSteuerberatung() {
  return <span style={{ ...leise, fontWeight: 700 }}>Zusammenstellung aus der Buchhaltung, keine Steuerberatung.</span>;
}

type Zeile = { kategorie: Kategorie; konto: string; betrag: number; zusatz?: string };

/** Aufstellung nach Anlage-V-Gruppen; jede Gruppe klappt ihre Konten auf. */
export function AufstellungKarte({ titel, rechts, posten, nurKosten = false }: { titel: string; rechts?: string; posten: readonly Zeile[]; nurKosten?: boolean }) {
  const [offen, setOffen] = useState<Kategorie | null>(null);
  const summe = (k: Kategorie) => posten.filter((p) => p.kategorie === k).reduce((s, p) => s + p.betrag, 0);
  const einnahmen = EINNAHME_KATEGORIEN.reduce((s, k) => s + summe(k), 0);
  const kosten = KOSTEN_KATEGORIEN.reduce((s, k) => s + summe(k), 0);
  const gruppen = (nurKosten ? KOSTEN_KATEGORIEN : [...EINNAHME_KATEGORIEN, ...KOSTEN_KATEGORIEN]).filter((k) => posten.some((p) => p.kategorie === k));
  return (
    <InnenKarte>
      <KartenTitel icon={Scales} rechts={rechts}>{titel}</KartenTitel>
      <KpiReihe minBreite={150}>
        {!nurKosten && <KpiTile label="Einnahmen" wert={eur(einnahmen)} ton="ok" />}
        <KpiTile label="Werbungskosten" wert={eur(kosten)} sub="ohne AfA" />
        {!nurKosten && <KpiTile label="Überschuss" wert={eur(einnahmen - kosten)} ton={einnahmen - kosten >= 0 ? 'ok' : 'bad'} />}
      </KpiReihe>
      <div className="flex flex-col" style={{ gap: 2 }}>
        {gruppen.map((k) => {
          const auf = offen === k;
          const zeilen = posten.filter((p) => p.kategorie === k);
          return (
            <div key={k} className="flex flex-col">
              <button type="button" onClick={() => setOffen(auf ? null : k)} aria-expanded={auf}
                className="flex items-center cursor-pointer" style={{ gap: 8, padding: '9px 6px', border: 'none', background: 'none', color: 'var(--ink)', textAlign: 'left', borderRadius: 10 }}>
                {auf ? <CaretDown size={14} /> : <CaretRight size={14} />}
                <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{KATEGORIE_TEXT[k]}</span>
                <span style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{eur(summe(k))}</span>
              </button>
              {auf && zeilen.map((p) => (
                <div key={p.konto + (p.zusatz ?? '')} className="flex items-center" style={{ gap: 8, padding: '4px 6px 4px 28px', fontSize: 12, color: 'var(--ink2)' }}>
                  <span style={{ flex: 1 }}>{p.konto}{p.zusatz && <span style={{ color: 'var(--ink3)' }}> · {p.zusatz}</span>}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{eur(p.betrag)}</span>
                </div>
              ))}
            </div>
          );
        })}
        {gruppen.length === 0 && <span style={leise}>Keine Zahlungen in diesem Jahr.</span>}
      </div>
    </InnenKarte>
  );
}

/** Was bewusst nicht in die Anlage V geht, und Rechnungen ohne Zahlung. */
export function NichtAnlageVKarte({ ergebnis }: { ergebnis: ObjektErgebnis }) {
  const nicht = (Object.entries(ergebnis.nichtAnlageV) as [NichtAnlageV, number][]).filter(([, v]) => Math.abs(v) >= 0.005);
  if (!nicht.length && !ergebnis.offeneRechnungen.length) return null;
  return (
    <InnenKarte>
      <KartenTitel>Nicht in der Anlage V</KartenTitel>
      {nicht.map(([k, v]) => (
        <div key={k} className="flex" style={{ gap: 8, fontSize: 13 }}>
          <span style={{ flex: 1 }}>{NICHT_ANLAGE_V_TEXT[k]}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{eur(v)}</span>
        </div>
      ))}
      {ergebnis.offeneRechnungen.length > 0 && <>
        <span style={{ ...leise, marginTop: 6 }}>Rechnungen ohne Zahlung (zählen erst, wenn bezahlt):</span>
        {ergebnis.offeneRechnungen.map((o) => (
          <div key={o.buchung.nummer + o.konto} className="flex" style={{ gap: 8, fontSize: 13 }}>
            <span style={{ flex: 1 }}>{o.konto} <span style={{ color: 'var(--ink3)' }}>· {o.buchung.nummer} · {o.buchung.text}</span></span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{eur(o.betrag)}</span>
          </div>
        ))}
      </>}
    </InnenKarte>
  );
}

const KAT_OPTIONEN = [...EINNAHME_KATEGORIEN, ...KOSTEN_KATEGORIEN].map((k) => ({ key: k, label: KATEGORIE_KURZ[k] }));

/** Ungeklärte Buchungen: Büro ordnet zu (Kategorie, Jahr, Zinsanteil) oder schließt aus. Nichts wird geraten. */
export function UngeklaertKarte({ objektId, ergebnis, klaerungen, onGeaendert, zeige }: {
  objektId: string; ergebnis: ObjektErgebnis; klaerungen: readonly KlaerungZeile[]; onGeaendert: () => void; zeige: (t: string, f?: boolean) => void;
}) {
  const [bearbeite, setBearbeite] = useState<string | null>(null);
  const geklaert = klaerungen.filter((k) => ergebnis.posten.some((p) => p.buchungen.includes(k.schluessel)) || k.jahr === ergebnis.jahr || k.aktion === 'ausschliessen');
  return (
    <InnenKarte>
      <KartenTitel icon={Question} rechts={ergebnis.ungeklaert.length ? `${ergebnis.ungeklaert.length} offen` : 'alles geklärt'}>Ungeklärte Buchungen {ergebnis.jahr}</KartenTitel>
      {ergebnis.ungeklaert.length === 0 && <span style={leise}>Alle Zahlungen dieses Jahres sind eindeutig zugeordnet.</span>}
      {ergebnis.ungeklaert.map((u) => (
        <div key={u.schluessel} className="immo-innen flex flex-col" style={{ borderRadius: 16, padding: 12, gap: 8 }}>
          <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <Chip ton="warn">{u.grund}</Chip>
            <span style={{ flex: 1, minWidth: 160, fontSize: 13 }}>{u.buchung.text || '–'}</span>
            <span style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{eur(u.betrag)}</span>
          </div>
          <span style={leise}>Nr. {u.buchung.nummer} · gezahlt {datum(u.buchung.wertstellung)} · betrifft {datum(u.buchung.abgrenzung)} · {u.buchung.soll_konto} → {u.buchung.haben_konto}</span>
          {bearbeite === u.schluessel
            ? <KlaerungFormular objektId={objektId} u={u} jahr={ergebnis.jahr} onFertig={(text) => { setBearbeite(null); zeige(text); onGeaendert(); }} onFehler={(t) => zeige(t, true)} onAbbrechen={() => setBearbeite(null)} />
            : <div className="flex flex-wrap" style={{ gap: 8 }}>
                <ImmoButton art="glas" groesse={32} icon={Check} onClick={() => setBearbeite(u.schluessel)}>Klären</ImmoButton>
              </div>}
        </div>
      ))}
      {geklaert.length > 0 && <>
        <span style={{ ...leise, marginTop: 6 }}>Vom Büro geklärt:</span>
        {geklaert.map((k) => (
          <div key={k.id} className="flex flex-wrap items-center" style={{ gap: 8, fontSize: 12 }}>
            <Chip ton={k.aktion === 'ausschliessen' ? 'neutral' : 'ok'}>{k.aktion === 'ausschliessen' ? 'ausgeschlossen' : KATEGORIE_KURZ[k.kategorie as Kategorie] ?? 'zugeordnet'}</Chip>
            <span style={{ flex: 1, color: 'var(--ink2)' }}>Nr. {k.schluessel.split('|')[0]}{k.jahr ? ` · Jahr ${k.jahr}` : ''}{k.zins != null ? ` · Zins ${eur(k.zins)}` : ''}{k.notiz ? ` · ${k.notiz}` : ''}</span>
            <ImmoButton art="glas" groesse={32} icon={ArrowCounterClockwise} aria-label="Klärung zurücknehmen"
              onClick={() => { void loescheKlaerung(k.id).then(() => { zeige('Klärung zurückgenommen'); onGeaendert(); }, (e: Error) => zeige(e.message, true)); }}>Zurück</ImmoButton>
          </div>
        ))}
      </>}
    </InnenKarte>
  );
}

function KlaerungFormular({ objektId, u, jahr, onFertig, onFehler, onAbbrechen }: {
  objektId: string; u: Ungeklaert; jahr: number; onFertig: (t: string) => void; onFehler: (t: string) => void; onAbbrechen: () => void;
}) {
  const darlehen = u.vorschlag === 'schuldzinsen';
  const korrektur = /welches Jahr/.test(u.grund);
  const [kat, setKat] = useState<Kategorie | null>(u.vorschlag ?? (korrektur ? null : null));
  const [j, setJ] = useState(String(jahr));
  const [zins, setZins] = useState('');
  const [notiz, setNotiz] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const zinsZahl = zins.trim() ? Number(zins.replace(/\./g, '').replace(',', '.')) : null;
  const jahrZahl = Number(j);
  const gueltig = Number.isInteger(jahrZahl) && jahrZahl > 2000 && (korrektur || !!kat) && (!darlehen || kat !== 'schuldzinsen' || (zinsZahl != null && zinsZahl >= 0));
  const sichere = async (aktion: 'zuordnen' | 'ausschliessen') => {
    setLaeuft(true);
    try {
      await speichereKlaerung(objektId, { schluessel: u.schluessel, aktion, kategorie: aktion === 'zuordnen' && !korrektur ? kat : null,
        jahr: aktion === 'zuordnen' ? jahrZahl : null, zins: aktion === 'zuordnen' && darlehen && kat === 'schuldzinsen' ? zinsZahl : null, notiz });
      onFertig(aktion === 'ausschliessen' ? 'Buchung ausgeschlossen' : 'Buchung zugeordnet');
    } catch (e) { onFehler(`Nicht gespeichert: ${(e as Error).message}`); setLaeuft(false); }
  };
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {!korrektur && <Auswahl label="Gehört zu" optionen={KAT_OPTIONEN} wert={kat} onChange={setKat} />}
      {korrektur && <span style={leise}>Korrektur: Sie zählt in dem Jahr, das hier eingetragen ist, und bucht zwischen den Konten der Buchung um.</span>}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <div style={{ flex: '0 1 140px' }}><TextFeld label="Steuerjahr" inputMode="numeric" wert={j} onChange={setJ} /></div>
        {darlehen && kat === 'schuldzinsen' && <div style={{ flex: '0 1 180px' }}><TextFeld label="Zinsanteil" inputMode="decimal" suffix="€" wert={zins} onChange={setZins} placeholder="z. B. 182,25" /></div>}
        <div style={{ flex: '1 1 220px' }}><TextFeld label="Notiz (optional)" wert={notiz} onChange={setNotiz} placeholder="z. B. laut Kontoauszug" /></div>
      </div>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton art="akzent" groesse={36} icon={Check} disabled={!gueltig || laeuft} onClick={() => void sichere('zuordnen')}>Zuordnen</ImmoButton>
        <ImmoButton art="glas" groesse={36} icon={Prohibit} disabled={laeuft} onClick={() => void sichere('ausschliessen')}>Nicht berücksichtigen</ImmoButton>
        <ImmoButton art="glas" groesse={36} disabled={laeuft} onClick={onAbbrechen}>Abbrechen</ImmoButton>
      </div>
    </div>
  );
}

const SCHLUESSEL_SEG = [{ key: 'mea', label: 'MEA' }, { key: 'einheit', label: 'Je Einheit' }, { key: 'fest', label: 'Fest' }] as const;

/** WEG: Verteilerschlüssel je Kostenkonto (Standard MEA) und die Verteilung auf die Einheiten. */
export function WegSchluesselKarte({ objektId, ergebnis, einheiten, schluessel, verteilung, onGeaendert, zeige }: {
  objektId: string; ergebnis: ObjektErgebnis; einheiten: readonly WegEinheit[]; schluessel: readonly Kostenschluessel[];
  verteilung: WegVerteilung; onGeaendert: () => void; zeige: (t: string, f?: boolean) => void;
}) {
  const konten = [...new Set(ergebnis.posten.map((p) => p.konto))];
  const [fest, setFest] = useState<string | null>(null);
  const [anteile, setAnteile] = useState<Record<string, string>>({});
  const art = (konto: string): SchluesselArt => schluessel.find((s) => s.konto === konto)?.art ?? 'mea';
  const setze = async (konto: string, a: SchluesselArt, werte: Record<string, number> | null = null) => {
    try { await speichereSchluessel(objektId, konto, a, werte); zeige(`„${konto}“: ${a === 'mea' ? 'nach MEA' : a === 'einheit' ? 'je Einheit' : 'feste Anteile'}`); setFest(null); onGeaendert(); }
    catch (e) { zeige(`Nicht gespeichert: ${(e as Error).message}`, true); }
  };
  const oeffneFest = (konto: string) => {
    const s = schluessel.find((x) => x.konto === konto);
    setAnteile(Object.fromEntries(einheiten.map((e) => [e.einheit_id, String(s?.anteile?.[e.einheit_id] ?? '')])));
    setFest(konto);
  };
  return (
    <InnenKarte>
      <KartenTitel icon={Scales} rechts="Standard: MEA">Verteilung auf die Einheiten</KartenTitel>
      <span style={leise}>Schlüssel wie in der Hausgeldabrechnung. MEA: {einheiten.map((e) => `${e.bezeichnung} ${e.mea ?? '–'}`).join(' · ')}</span>
      {verteilung.hinweise.map((h) => <span key={h} style={{ fontSize: 12, color: 'var(--warn)' }}>{h}</span>)}
      <div className="flex flex-col" style={{ gap: 6 }}>
        {konten.map((k) => (
          <div key={k} className="flex flex-col" style={{ gap: 6 }}>
            <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
              <span style={{ flex: '1 1 200px', fontSize: 13 }}>{k}</span>
              <SegmentedControl ariaLabel={`Schlüssel ${k}`} hoehe={30} wert={fest === k ? 'fest' : art(k)}
                onChange={(a) => (a === 'fest' ? oeffneFest(k) : void setze(k, a))} segmente={SCHLUESSEL_SEG} />
            </div>
            {fest === k && (
              <div className="flex flex-wrap items-end" style={{ gap: 8, paddingLeft: 12 }}>
                {einheiten.map((e) => (
                  <div key={e.einheit_id} style={{ flex: '0 1 120px' }}>
                    <TextFeld label={e.bezeichnung} inputMode="decimal" wert={anteile[e.einheit_id] ?? ''} onChange={(v) => setAnteile((a) => ({ ...a, [e.einheit_id]: v }))} />
                  </div>
                ))}
                <ImmoButton art="akzent" groesse={36} icon={Check} onClick={() => {
                  const w = Object.fromEntries(Object.entries(anteile).map(([id, v]) => [id, Number(v.replace(',', '.')) || 0]));
                  if (!Object.values(w).some((v) => v > 0)) { zeige('Mindestens ein Anteil größer 0', true); return; }
                  void setze(k, 'fest', w);
                }}>Speichern</ImmoButton>
                <ImmoButton art="glas" groesse={36} onClick={() => setFest(null)}>Abbrechen</ImmoButton>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-col" style={{ gap: 4, marginTop: 6 }}>
        {verteilung.einheiten.map((e) => (
          <div key={e.einheit_id} className="flex" style={{ gap: 8, fontSize: 13 }}>
            <span style={{ flex: 1, fontWeight: 700 }}>{e.bezeichnung}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{eur(e.werbungskosten)}</span>
          </div>
        ))}
      </div>
    </InnenKarte>
  );
}
